import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, RefreshCw, ArrowRight } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { useNavigate } from "react-router-dom";
import { useRefresh } from "@/contexts/RefreshContext";
import { insightsService } from "@/services/insightsService";
import { wellnessService } from "@/services/wellnessService";

const AIInsightsWidget = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { lastCheckInTime } = useRefresh();
  const [insights, setInsights] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [checkInCount, setCheckInCount] = useState(0);
  const [hasEnoughData, setHasEnoughData] = useState(false);

  const loadInsights = async (forceRefresh = false) => {
    if (!user) return;

    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Loading AI insights...', { forceRefresh, userId: user.id });
    }
    setIsLoading(true);
    try {
      // First check how many check-ins the user has
      // apiService returns data directly, not { success, data }
      const statsData = await wellnessService.getStats().catch(() => ({ totalCheckIns: 0 }));
      const count = statsData?.totalCheckIns || 0;
      setCheckInCount(count);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Check-in count:', count);
      }
      
      // Enable insights after just 1 check-in for immediate value
      if (count >= 1) {
        setHasEnoughData(true);
        
        if (count === 1) {
          // Provide encouraging first-time insights
          setInsights([
            "Nice! You're starting to build a picture of your patterns. One check-in down, and I'm already paying attention.",
            "The real magic happens when you check in consistently - that's when I can spot the trends you might miss."
          ]);
        } else {
          // ALIGNED WITH web_old: Use getGenerateInsights() which returns string[] directly
          // Backend endpoint: GET /insights/generate-insights
          // MIGRATED FROM web_old/src/services/openaiService.ts::generateInsights()
          const aiInsights = await insightsService.getGenerateInsights().catch(() => null);
          
          if (aiInsights && Array.isArray(aiInsights) && aiInsights.length > 0) {
            // ALIGNED WITH web_old: Directly use string[] and slice to 2 insights
            setInsights(aiInsights.slice(0, 2));
          } else {
            // Fallback insights (matching web_old fallback behavior)
            setInsights([
              "Keep checking in daily to unlock personalized insights about your patterns and productivity.",
              "Your consistency is building valuable data - I'll have deeper insights soon!"
            ]);
          }
        }
      } else {
        setHasEnoughData(false);
        setInsights([]);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading AI insights:', error);
      }
      setInsights([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInsights();
  }, [user]);

  // Refresh insights when a new check-in is submitted
  useEffect(() => {
    if (lastCheckInTime && user) {
      console.log('🔔 Check-in detected, refreshing insights...', new Date(lastCheckInTime));
      loadInsights(true);
    }
  }, [lastCheckInTime]);

  const navigateToFullInsights = () => {
    navigate('/patterns');
  };

  // Don't render the widget if user doesn't have enough data
  if (!hasEnoughData && !isLoading) {
    return (
      <Card className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-card dark:to-muted/50 border-purple-200 dark:border-border">
        <CardContent className="p-6 text-center">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          <h3 className="font-semibold mb-2">AI Insights Coming Soon</h3>
          <p className="text-sm text-muted-foreground mb-4">
            I'll start noticing patterns once you do your first check-in. The more you check in, the better I'll understand what makes you tick.
          </p>
          <div className="flex justify-center items-center gap-2">
            <Badge variant="outline" className="text-xs">
              Ready to unlock with first check-in
            </Badge>
          </div>
          <Button 
            onClick={() => navigate('/check-in')} 
            size="sm" 
            className="mt-4"
            variant="outline"
          >
            Continue Journey
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-card dark:to-muted/50 border-purple-200 dark:border-border hover:shadow-md transition-shadow ai-insights-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-purple-100 dark:bg-muted rounded-lg">
              <Sparkles className="h-4 w-4 text-purple-600 dark:text-primary" />
            </div>
            <div>
              <CardTitle className="text-base text-foreground">✨ AI Insights</CardTitle>
              <CardDescription className="text-xs">
                What I'm noticing about you
              </CardDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => loadInsights(true)}
            disabled={isLoading}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-12 bg-white/60 animate-pulse rounded-lg"></div>
            ))}
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {insights.map((insight, index) => (
                <div 
                  key={index} 
                  className="bg-white/70 dark:bg-card/70 p-3 rounded-lg border border-purple-100 dark:border-border"
                >
                  <p className="text-sm text-gray-700 dark:text-foreground leading-relaxed">
                    {insight}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="pt-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={navigateToFullInsights}
                className="w-full text-purple-700 dark:text-primary border-purple-200 dark:border-border hover:bg-purple-50 dark:hover:bg-muted"
              >
                <Sparkles className="h-3 w-3 mr-2" />
                See All Patterns
                <ArrowRight className="h-3 w-3 ml-2" />
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AIInsightsWidget;